import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Calendar, TrendingUp, Users, Clock, BarChart as BarChartIcon } from 'lucide-react';
import { toast } from 'sonner';

const COLORS = ['#14b8a6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#3b82f6'];

const leaveTypeLabels = {
  sick_leave: 'Sick Leave', casual_leave: 'Casual Leave', vacation: 'Vacation',
  earned_leave: 'Earned Leave', maternity_leave: 'Maternity', paternity_leave: 'Paternity', unpaid_leave: 'Unpaid',
};

export const DashboardAnalytics = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashStats, setDashStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const isAdmin = user?.role === 'school_admin' || user?.role === 'hr';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [dashRes, analyticsRes] = await Promise.all([
        api.get('/admin/dashboard'),
        api.get('/admin/analytics'),
      ]);
      setDashStats(dashRes.data.data);
      setAnalytics(analyticsRes.data.data);
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="analytics-loading">
        <div className="h-8 w-8 border-2 border-teal-500/30 border-t-teal-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="text-center py-12" data-testid="analytics-no-access">
        <p className="text-slate-600">Analytics dashboard is available for admins only.</p>
      </div>
    );
  }

  const totalRequests = (dashStats?.pending_requests || 0) + (dashStats?.approved_this_month || 0) + (dashStats?.rejected_this_month || 0);
  const approvalRate = totalRequests > 0
    ? ((dashStats?.approved_this_month || 0) / Math.max(1, (dashStats?.approved_this_month || 0) + (dashStats?.rejected_this_month || 0)) * 100).toFixed(1)
    : '0';

  const typeData = (analytics?.leave_type_distribution || []).map(t => ({
    name: leaveTypeLabels[t.name] || t.name?.replace('_', ' ') || 'Other',
    value: t.value,
  }));

  const statusData = analytics?.status_distribution || [];

  return (
    <div className="space-y-6" data-testid="analytics-dashboard">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-teal-500 to-emerald-500 flex items-center justify-center shadow-md shadow-teal-500/20">
          <BarChartIcon className="h-5 w-5 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-800" data-testid="analytics-title">Analytics Dashboard</h1>
          <p className="text-sm text-slate-500">Leave management insights and trends</p>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        {[
          { title: 'Total Requests', value: totalRequests, sub: 'This month', icon: Calendar, gradient: 'from-teal-500 to-emerald-500' },
          { title: 'Approval Rate', value: `${approvalRate}%`, sub: 'Approved vs rejected', icon: TrendingUp, gradient: 'from-emerald-500 to-green-500' },
          { title: 'Pending', value: dashStats?.pending_requests || 0, sub: 'Awaiting approval', icon: Clock, gradient: 'from-amber-400 to-orange-500' },
          { title: 'Active Staff', value: dashStats?.active_users || 0, sub: `of ${dashStats?.total_users || 0} total`, icon: Users, gradient: 'from-blue-500 to-indigo-500' },
        ].map((card, i) => {
          const Icon = card.icon;
          return (
            <div key={i} className="stat-card p-5 relative overflow-hidden group" data-testid={`metric-${i}`}>
              <div className={`absolute top-0 right-0 w-24 h-24 rounded-full bg-gradient-to-br ${card.gradient} opacity-[0.07] -translate-y-6 translate-x-6`} />
              <div className="flex items-center justify-between mb-4 relative">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">{card.title}</p>
                <div className={`h-10 w-10 rounded-xl bg-gradient-to-br ${card.gradient} flex items-center justify-center shadow-lg shadow-slate-200`}>
                  <Icon className="h-5 w-5 text-white" />
                </div>
              </div>
              <p className="text-3xl font-bold text-slate-800 mb-1 relative">{card.value}</p>
              <p className="text-xs text-slate-400 relative">{card.sub}</p>
            </div>
          );
        })}
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="glass-card" data-testid="chart-monthly-trend">
          <CardHeader>
            <CardTitle className="text-slate-800">Monthly Trend</CardTitle>
            <CardDescription>Leave requests by status over time</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={analytics?.monthly_trend || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="month" stroke="#94a3b8" fontSize={12} />
                <YAxis stroke="#94a3b8" fontSize={12} />
                <Tooltip contentStyle={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '12px', fontSize: 13 }} />
                <Legend />
                <Line type="monotone" dataKey="approved" stroke="#10b981" strokeWidth={2.5} dot={{ r: 4 }} name="Approved" />
                <Line type="monotone" dataKey="pending" stroke="#f59e0b" strokeWidth={2.5} dot={{ r: 4 }} name="Pending" />
                <Line type="monotone" dataKey="rejected" stroke="#ef4444" strokeWidth={2.5} dot={{ r: 4 }} name="Rejected" />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="glass-card" data-testid="chart-leave-types">
          <CardHeader>
            <CardTitle className="text-slate-800">Leave Type Distribution</CardTitle>
            <CardDescription>Breakdown by leave categories</CardDescription>
          </CardHeader>
          <CardContent>
            {typeData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={typeData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={90}
                    dataKey="value"
                  >
                    {typeData.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '12px', fontSize: 13 }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[280px] flex items-center justify-center text-slate-400 text-sm">No leave data yet</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="glass-card" data-testid="chart-departments">
          <CardHeader>
            <CardTitle className="text-slate-800">Requests by Department</CardTitle>
            <CardDescription>Leave distribution across departments</CardDescription>
          </CardHeader>
          <CardContent>
            {(analytics?.department_stats || []).length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={analytics.department_stats}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="department" stroke="#94a3b8" fontSize={12} />
                  <YAxis stroke="#94a3b8" fontSize={12} />
                  <Tooltip contentStyle={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '12px', fontSize: 13 }} />
                  <Bar dataKey="requests" fill="#14b8a6" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[280px] flex items-center justify-center text-slate-400 text-sm">No department data yet</div>
            )}
          </CardContent>
        </Card>

        <Card className="glass-card" data-testid="chart-status">
          <CardHeader>
            <CardTitle className="text-slate-800">Status Overview</CardTitle>
            <CardDescription>All-time leave request statuses</CardDescription>
          </CardHeader>
          <CardContent>
            {statusData.length > 0 ? (
              <div className="space-y-4 pt-4">
                {statusData.map((s, i) => {
                  const total = statusData.reduce((sum, x) => sum + x.count, 0);
                  const pct = total > 0 ? (s.count / total) * 100 : 0;
                  const colors = { approved: '#10b981', pending: '#f59e0b', rejected: '#ef4444' };
                  const bgColors = { approved: 'bg-emerald-50', pending: 'bg-amber-50', rejected: 'bg-rose-50' };
                  return (
                    <div key={i} className={`p-4 rounded-xl ${bgColors[s.name] || 'bg-slate-50'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-semibold text-slate-700 capitalize">{s.name}</span>
                        <span className="text-sm font-bold text-slate-800">{s.count} ({pct.toFixed(0)}%)</span>
                      </div>
                      <div className="h-2 rounded-full bg-white/80 overflow-hidden">
                        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: colors[s.name] || '#64748b' }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="h-[280px] flex items-center justify-center text-slate-400 text-sm">No status data yet</div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DashboardAnalytics;
